// RecentTickets.jsx
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, User, ArrowRight, Plus } from 'lucide-react';
import StatusBadge, { PriorityBadge } from '@components/common/StatusBadge';

const RecentTickets = ({ data }) => {
  const defaultTickets = [
    {
      id: 1,
      ticket_number: 'RDX-2025-000001',
      subject: 'Customer payment overdue for Invoice #INV-2024-1234',
      status: 'in_progress',
      priority: 'high',
      created_at: '2025-02-05T10:30:00Z',
      assigned_to: { first_name: 'Amit', last_name: 'Patel' },
      customer: { first_name: 'Ramesh', last_name: 'Agarwal' }
    },
    {
      id: 2,
      ticket_number: 'RDX-2025-000002',
      subject: 'Delayed delivery to Chennai - Shipment SHP-2025-001',
      status: 'critical',
      priority: 'critical',
      created_at: '2025-02-05T09:15:00Z',
      assigned_to: { first_name: 'Arjun', last_name: 'Nair' },
      customer: { first_name: 'Sanjay', last_name: 'Mehta' }
    },
    {
      id: 3,
      ticket_number: 'RDX-2025-000003',
      subject: 'Customer complaint about damaged goods',
      status: 'not_started',
      priority: 'high',
      created_at: '2025-02-05T08:45:00Z',
      assigned_to: { first_name: 'Kavya', last_name: 'Reddy' },
      customer: { first_name: 'Neha', last_name: 'Kapoor' }
    },
    {
      id: 4,
      ticket_number: 'RDX-2025-000004',
      subject: 'Vehicle breakdown - MH-12-XY-5678',
      status: 'in_progress',
      priority: 'critical',
      created_at: '2025-02-05T07:20:00Z',
      assigned_to: { first_name: 'Rohit', last_name: 'Das' },
      customer: null
    },
    {
      id: 5,
      ticket_number: 'RDX-2025-000005',
      subject: 'GST reconciliation discrepancy for December 2024',
      status: 'not_started',
      priority: 'medium',
      created_at: '2025-02-04T16:30:00Z',
      assigned_to: { first_name: 'Sunita', last_name: 'Verma' },
      customer: null
    }
  ];

  const tickets = Array.isArray(data) && data.length ? data : defaultTickets;

  const formatTimeAgo = timestamp => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const styles = {
    panel: {
      backgroundColor: '#fff',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)',
      border: '1px solid #eef2f6',
      overflow: 'hidden'
    },
    header: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid #f3f6f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    titleGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    title: {
      margin: 0,
      fontSize: '1.125rem',
      fontWeight: 700,
      color: 'var(--gray-900)'
    },
    subtitle: {
      margin: 0,
      fontSize: '0.875rem',
      color: 'var(--gray-500)'
    },
    viewAllLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      textDecoration: 'none',
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--primary-600)'
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      divideStyle: 'solid'
    },
    listItem: {
      padding: '1rem 1.5rem',
      cursor: 'pointer',
      transition: 'background-color 150ms, transform 150ms'
    },
    listItemHover: {
      backgroundColor: '#f8fafc'
    },
    ticketTopRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.65rem',
      marginBottom: '0.5rem'
    },
    ticketNumber: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--primary-600)'
    },
    subject: {
      margin: '0 0 0.5rem 0',
      fontSize: '1rem',
      fontWeight: 600,
      color: 'var(--gray-900)',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical'
    },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      fontSize: '0.875rem',
      color: 'var(--gray-500)'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem'
    },
    rightIconWrap: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    emptyState: {
      padding: '3rem 1.5rem',
      textAlign: 'center'
    },
    emptyIconWrap: {
      width: '4rem',
      height: '4rem',
      borderRadius: '9999px',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1rem auto'
    },
    createBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      background: 'var(--primary-600)',
      color: 'white',
      borderRadius: '0.5rem',
      textDecoration: 'none',
      fontWeight: 600
    }
  };

  return (
    <motion.div
      style={styles.panel}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
    >
      <div style={styles.header}>
        <div style={styles.titleGroup}>
          <h3 style={styles.title}>Recent Tickets</h3>
          <p style={styles.subtitle}>Latest ticket activities and updates</p>
        </div>

        <Link to="/tickets" style={styles.viewAllLink}>
          <span>View all</span>
          <ArrowRight style={{ width: '1rem', height: '1rem', color: 'currentColor' }} />
        </Link>
      </div>

      <div>
        {tickets.length > 0 ? (
          tickets.map((ticket, index) => {
            const itemKey = ticket.id;
            const timeAgo = formatTimeAgo(ticket.created_at);
            return (
              <motion.div
                key={itemKey}
                style={styles.listItem}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.28, delay: index * 0.06 }}
                whileHover={{ x: 4 }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Link to={`/tickets/${ticket.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.ticketTopRow}>
                        <span style={styles.ticketNumber}>{ticket.ticket_number}</span>
                        <div style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                          <StatusBadge status={ticket.status} size="sm" />
                          <PriorityBadge priority={ticket.priority} size="sm" />
                        </div>
                      </div>

                      <h4 style={styles.subject}>{ticket.subject}</h4>

                      <div style={styles.metaRow}>
                        {ticket.assigned_to && (
                          <div style={styles.metaItem}>
                            <User style={{ width: '1rem', height: '1rem', color: 'var(--gray-400)' }} />
                            <span>
                              {ticket.assigned_to.first_name} {ticket.assigned_to.last_name}
                            </span>
                          </div>
                        )}

                        <div style={styles.metaItem}>
                          <Clock style={{ width: '1rem', height: '1rem', color: 'var(--gray-400)' }} />
                          <span>{timeAgo}</span>
                        </div>

                        {ticket.customer && (
                          <div style={styles.metaItem}>
                            <span style={{ color: 'var(--gray-300)' }}>•</span>
                            <span>
                              {ticket.customer.first_name} {ticket.customer.last_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={styles.rightIconWrap} aria-hidden="true">
                      <ArrowRight style={{ width: '1.25rem', height: '1.25rem', color: 'var(--gray-400)' }} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIconWrap}>
              <Clock style={{ width: '1.75rem', height: '1.75rem', color: 'var(--gray-400)' }} />
            </div>

            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: 700, color: 'var(--gray-900)' }}>
              No recent tickets
            </h3>

            <p style={{ margin: 0, color: 'var(--gray-500)', marginBottom: '1rem' }}>
              When tickets are created, they'll appear here.
            </p>

            <Link to="/tickets/create" style={styles.createBtn}>
              <Plus style={{ width: '1rem', height: '1rem' }} />
              <span>Create Ticket</span>
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RecentTickets;
